import { jt, t } from "ttag";

import { useMessenger } from "@/app/hooks/use-messenger";
import { SIZE } from "@/shared/models/legacy-constants";
import type { ModalProps } from "@/shared/models/legacy-types";
import {
  Button,
  ChatLineIcon,
  Dialog,
  HeartsIcon,
  IconWrapper,
  MapPointIcon,
  SmartphoneIcon,
  UserIcon,
  WebIcon,
} from "@/shared/ui";

export const WellBeingModal = ({ show, onClose }: ModalProps) => {
  const {
    actions: { showMessenger },
  } = useMessenger();

  const telehealthBestPracticesWarning = (
    <strong key="telehealth-best-practices-warning">{t`Unfortunately, the behavioral health history that you have identified is not considered to be best practice when treated through telehealth.`}</strong>
  );
  const newLine = <br key="new-line" className="block pt-5 content-['']" />;

  const handleContactSupport = () => {
    onClose();
    showMessenger();
  };

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <Dialog.Content>
        <Dialog.Header icon={HeartsIcon}>
          <Dialog.Title>{t`Prioritizing your well-being`}</Dialog.Title>

          <Dialog.Description>{jt`Our commitment is to ensure you receive the most appropriate and evidence-based care from our team of medical professionals. ${telehealthBestPracticesWarning}
            ${newLine}
            In such cases, prioritizing your safety, we recommend seeking care from a trusted local provider for more beneficial results. We apologize for any inconvenience this may cause and truly value the opportunity you've given us to support you.`}</Dialog.Description>
        </Dialog.Header>

        <div className="w-full border border-neutral-400" />

        <div className="flex flex-col justify-start gap-3 text-neutral-950">
          <h4 className="font-medium md:text-sm">{t`To assist you further, we've compiled a list of resources tailored to your potential needs and concerns mentioned during your visit:`}</h4>

          <div className="flex grid-cols-3 flex-col gap-2 md:grid">
            <div className="flex flex-col gap-1 rounded-lg bg-neutral-200 p-2 pb-3.5 md:min-h-32">
              <header className="flex items-center gap-1">
                <IconWrapper size={SIZE.X_SMALL}>
                  <SmartphoneIcon />
                </IconWrapper>
                <h5 className="font-medium md:text-xs">{t`Phone call`}</h5>
              </header>
              <ul className="list-outside list-disc pl-6 text-sm md:text-xs">
                <li>{t`988 - Crisis Support Lifeline`}</li>
                <li>{t`1-800-273-8255 - National Crisis Support`}</li>
                <li>{t`1-866-488-7386 or text START to 678678. A national 24-hour, toll free confidential suicide hotline for LGBTQ youth`}</li>
              </ul>
            </div>

            <div className="flex flex-col gap-1 rounded-lg bg-neutral-200 p-2 pb-3.5 md:min-h-32">
              <header className="flex items-center gap-1">
                <IconWrapper size={SIZE.X_SMALL}>
                  <ChatLineIcon />
                </IconWrapper>
                <h5 className="font-medium md:text-xs">{t`Text`}</h5>
              </header>
              <ul className="list-outside list-disc pl-6 text-sm md:text-xs">
                <li>{t`Text MHA to 741741 and you will be connected to a trained Crisis Counselor. Crisis Text Line provides free, text-based support 24/7.`}</li>
                <li>{t`Text 988 for immediate assistance.`}</li>
              </ul>
            </div>

            <div className="flex flex-col gap-1 rounded-lg bg-neutral-200 p-2 pb-3.5 md:min-h-32">
              <header className="flex items-center gap-1">
                <IconWrapper size={SIZE.X_SMALL}>
                  <WebIcon />
                </IconWrapper>
                <h5 className="font-medium md:text-xs">{t`Phone call`}</h5>
              </header>
              <ul className="list-outside list-disc pl-6 text-sm md:text-xs">
                <li>
                  <a
                    href="https://988lifeline.org/chat/"
                    className="underline underline-offset-2"
                  >{t`988 Lifeline Chat Online`}</a>
                </li>
                <li>
                  <a
                    href="https://988lifeline.org/"
                    className="underline underline-offset-2"
                  >{t`988 Lifeline`}</a>
                </li>
                <li>
                  <a
                    href="https://www.thetrevorproject.org/"
                    className="underline underline-offset-2"
                  >{t`The Trevor Project`}</a>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-1 rounded-lg bg-neutral-200 p-2 pb-3.5 md:min-h-32">
              <header className="flex items-center gap-1">
                <IconWrapper size={SIZE.X_SMALL}>
                  <UserIcon />
                </IconWrapper>
                <h5 className="font-medium md:text-xs">{t`Reduced cost in-person therapy in your area:`}</h5>
              </header>
              <ul className="list-outside list-disc pl-6 text-sm md:text-xs">
                <li>
                  <a
                    href="https://openpathcollective.org/"
                    className="underline underline-offset-2"
                  >{t`Open Path Collective: Find your therapist`}</a>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-1 rounded-lg bg-neutral-200 p-2 pb-3.5 md:min-h-32">
              <header className="flex items-center gap-1">
                <IconWrapper size={SIZE.X_SMALL}>
                  <MapPointIcon />
                </IconWrapper>
                <h5 className="font-medium md:text-xs">{t`Find an in-person provider in your local area:`}</h5>
              </header>
              <ul className="list-outside list-disc pl-6 text-sm md:text-xs">
                <li>
                  <a
                    href="https://www.psychologytoday.com/us"
                    className="underline underline-offset-2"
                  >{t`Psychology Today US`}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <Dialog.Footer>
          <Button size="lg" className="w-full" onClick={handleContactSupport}>
            {t`Contact LevelsRx support`}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
